import React from 'react';

import {
  Check as CheckFilledIcon,
  Clear as ClearFilledIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { Box, Typography } from '@mui/material';

import { DateFormatPatterns } from '@clearverify/constants';
import { formatDate } from '@clearverify/utils';

import {
  PermissiblePurposeStatus,
  type PermissiblePurposesItemFragment,
  usePermissiblePurposesListQuery,
} from 'src/api';
import { Tooltip } from 'src/components/common';
import type { TableColumn, TableRowAction } from 'src/components/table';
import { Table, TableThemes } from 'src/components/table';
import { ModalId } from 'src/constants/modals';
import { topInTableWithBorderStyles } from 'src/constants/styles';
import { usePaginationQueryVariables } from 'src/hooks/common';
import { usePermissiblePurposeUpdate } from 'src/hooks/permissiblePurposes';
import { useModal } from 'src/hooks/shared';
import { useVerifierById, useVerifierParamId } from 'src/hooks/verifier';

export const VerifierVerificationPermissiblePurposes: React.FC = () => {
  const verifierId = useVerifierParamId();
  const { verifier } = useVerifierById(verifierId);
  const paginationQueryVariables = usePaginationQueryVariables();

  const { data: permissiblePurposeDataList, loading } = usePermissiblePurposesListQuery({
    variables: {
      filter: {
        verifierId: { equals: verifier?.id },
      },
      ...paginationQueryVariables,
      orderBy: { status: { sort: 'Desc', nulls: 'Last' } },
    },
    notifyOnNetworkStatusChange: true,
  });

  const { updatePermissiblePurpose } = usePermissiblePurposeUpdate();

  const { openModal } = useModal();

  const approvePermissiblePurpose = React.useCallback(
    (permissiblePurpose: PermissiblePurposesItemFragment) => {
      if (permissiblePurpose.id) {
        updatePermissiblePurpose({
          variables: {
            id: permissiblePurpose.id,
            data: {
              status: PermissiblePurposeStatus.Approved,
              denialReason: null,
            },
          },
        });
      }
    },
    [updatePermissiblePurpose],
  );

  const handleDeclineModal = React.useCallback(
    (permissiblePurpose: PermissiblePurposesItemFragment) =>
      openModal(ModalId.PermissiblePurposeDeclineModal, {
        permissiblePurposeId: permissiblePurpose.id,
      }),
    [openModal],
  );

  const rowActions: Array<TableRowAction<PermissiblePurposesItemFragment>> = [
    {
      startIcon: <CheckFilledIcon color="action" style={{ margin: '0 10px 0 0' }} />,
      actionName: 'Approve',
      onAction: approvePermissiblePurpose,
      disabled: permissiblePurpose =>
        permissiblePurpose.status === PermissiblePurposeStatus.Approved,
    },
    {
      startIcon: <ClearFilledIcon color="error" style={{ margin: '0 10px 0 0' }} />,
      actionName: 'Decline',
      onAction: handleDeclineModal,
      disabled: permissiblePurpose =>
        permissiblePurpose.status === PermissiblePurposeStatus.Declined,
    },
  ];

  return (
    <Table
      extraTopNode={extraTopNode}
      dataSource={permissiblePurposeDataList?.permissiblePurposeList ?? []}
      columns={columns}
      rowActions={rowActions}
      hidePagination
      hideFooter
      loading={loading}
      tableTheme={TableThemes.withBorder}
    />
  );
};

const extraTopNode = (
  <Box sx={topInTableWithBorderStyles}>
    <Typography variant="subtitle1">Permissible Purposes</Typography>
  </Box>
);

const columns: Array<TableColumn<PermissiblePurposesItemFragment>> = [
  {
    title: 'Requested',
    key: 'createdAt',
    render: (_valueByDataPath, permissiblePurpose) => {
      return (
        <Typography variant="body2">
          {formatDate(permissiblePurpose.createdAt, DateFormatPatterns.shortDateWithSlash)}
        </Typography>
      );
    },
  },
  {
    title: 'Name',
    key: 'purposeValue',
    render: (_valueByDataPath, permissiblePurpose) => {
      return (
        <Typography variant="body2">
          {permissiblePurpose.permissiblePurposeReference.label}
        </Typography>
      );
    },
  },
  {
    title: 'Status',
    key: 'status',
    render: (_valueByDataPath, permissiblePurpose) => {
      return (
        <Box display="flex" gap="5px" alignItems="center">
          <Typography variant="body2">{permissiblePurpose.status}</Typography>
          {permissiblePurpose.status === PermissiblePurposeStatus.Declined && (
            <Tooltip title={permissiblePurpose.denialReason}>
              <InfoIcon
                color="action"
                sx={{
                  width: '16px',
                  height: '16px',
                  fontSize: '16px',
                }}
              />
            </Tooltip>
          )}
        </Box>
      );
    },
  },
];
